'use client';

/**
 * API namespace `products` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L6174-L6456).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const products = {
  /**
   * GET /api/seller/products?page=&limit=&status= — list the seller's
   * own products (paginated). Returns the seller's full view including
   * admin-only fields (`adminNotes`, `reviewedBy`, `reviewedAt`) so
   * the seller can see rejection reasons in the dashboard table.
   *
   * The `status` filter is optional — when omitted, all statuses
   * (including 'deleted' for audit) are returned.
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-081 Fase 3 §5.2, §4.1 (listSellerProducts),
   *           §6.3 (seller productos CRUD table).
   */
  async list(
    page = 0,
    limit = 20,
    signal?: AbortSignal,
    sellerId?: string,
  ): Promise<{ products: SellerProductResponse[]; total: number; page: number; limit: number }> {
    // Subtiendas (PR #1511 follow-up + Founder directive 2026-10-31 BE
    // honors the param): when `sellerId` is provided (the active store
    // selected via StoreSwitcher), append it as a query param so the BE
    // filters the seller's products by sub-store. The BE now verifies
    // ownership + uses it (resolveSellerId in _shared.ts).
    const qs = `page=${page}&limit=${limit}${
      sellerId ? `&sellerId=${encodeURIComponent(sellerId)}` : ''
    }`;
    return apiFetch<{ products: SellerProductResponse[]; total: number; page: number; limit: number }>(
      `/seller/products?${qs}`,
      { signal },
    );
  },
  /**
   * POST /api/seller/products — create a new product.
   *
   * Server defaults: `slug=slugify(title)` (retry on E11000 with `-2`,
   * `-3` suffix — Q3), `status='pending_approval'` (INV-MKT-20 — admin
   * must approve before the product appears in the public catalog),
   * `images=[]` (uploaded post-creation via `uploadImages`).
   *
   * Authz: `requireAuth` + `requireActiveSeller`. Zod
   * `productCreateSchema` cross-field rules (variations only for
   * physical, serviceConfig required for service, digitalConfig
   * required for digital, fulfillment.shippingOverride present iff
   * `inheritSeller=false`) are enforced server-side.
   *
   * Spec ref: SPEC-081 Fase 3 §5.2, §4.1 (createProduct),
   *           §3.1 (defaults), §0 decisions #1-#6.
   */
  async create(
    data: ProductCreateRequest,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): when `sellerId`
    // is provided (the active store selected via StoreSwitcher), append
    // it as a query param so the BE creates the product under the
    // SELECTED sub-store (NOT the primary store). The BE verifies
    // ownership before using it.
    sellerId?: string,
  ): Promise<SellerProductResponse> {
    const url = sellerId
      ? `/seller/products?sellerId=${encodeURIComponent(sellerId)}`
      : '/seller/products';
    return apiFetch<SellerProductResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * GET /api/seller/products/:productId — seller's own view of a single
   * product (includes admin-only fields).
   *
   * Authz: self only — backend verifies the product belongs to the
   * authenticated seller (`sellerId === req.user.id`).
   *
   * Spec ref: SPEC-081 Fase 3 §5.2, §4.1 (getProduct).
   */
  async get(
    productId: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<SellerProductResponse> {
    const url = sellerId
      ? `/seller/products/${encodeURIComponent(productId)}?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/${encodeURIComponent(productId)}`;
    return apiFetch<SellerProductResponse>(url, { signal });
  },
  /**
   * PUT /api/seller/products/:productId — partial update.
   *
   * Editing an 'approved' product resets its status to
   * 'pending_approval' (INV-MKT-20 — admin must re-approve). All
   * `ProductCreateRequest` fields are optional (partial update).
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only. Zod
   * `productUpdateSchema` validates the partial body.
   *
   * Spec ref: SPEC-081 Fase 3 §5.2, §4.1 (updateProduct),
   *           §3.1 (status reset on edit).
   */
  async update(
    productId: string,
    data: ProductUpdateRequest,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE update + ownership check use the SELECTED sub-store.
    sellerId?: string,
  ): Promise<SellerProductResponse> {
    const url = sellerId
      ? `/seller/products/${encodeURIComponent(productId)}?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/${encodeURIComponent(productId)}`;
    return apiFetch<SellerProductResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * DELETE /api/seller/products/:productId — soft delete.
   *
   * Sets `status='deleted'` (Q5 — soft delete for audit + dispute
   * trail in Fase 6). The product is hidden from the public catalog
   * + the seller's list (the seller list endpoint by default
   * excludes 'deleted' status unless an explicit `?status=deleted`
   * filter is passed — the seller dashboard uses that filter on the
   * "Eliminados" tab).
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only +
   * `requireRecent2FA` (TOTP 2FA verified within last 5 min — INV-SEC-06).
   * The frontend opens `TwoFactorFinancialDialog` on 403
   * `2FA_REQUIRED` (same pattern as the wallet withdraw flow).
   *
   * Spec ref: SPEC-081 Fase 3 §5.2, §4.1 (deleteProduct), §0 decision #5
   *           (soft delete), §3.1 (status='deleted');
   *           SPEC-NEGOCIO §9.6 (INV-SEC-06 — 2FA for sensitive ops).
   */
  async delete(
    productId: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<{ success: true; productId: string }> {
    const url = sellerId
      ? `/seller/products/${encodeURIComponent(productId)}?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/${encodeURIComponent(productId)}`;
    return apiFetch<{ success: true; productId: string }>(url, {
      method: 'DELETE',
      signal,
    });
  },
  /**
   * DELETE /api/seller/products/:productId/hard-delete — eliminación
   * DEFINITIVA (SPEC-HARD-DELETE-PRODUCT, PR #1165).
   *
   * Requisitos backend (SPEC §3): el producto debe estar previamente
   * soft-deleteado (status='deleted') + sin órdenes blocking. Purgea las
   * imágenes del CDN (best-effort) + AuditLog + UnifiedTransaction.
   * NO requiere 2FA (confirmación con texto 'ELIMINAR' en el frontend).
   *
   * HOTFIX (2026-10-05): este método faltaba en el namespace desde PR
   * #1165 — quedaba enmascarado por los merge markers de api-client.ts
   * (TS1185 hacia `any` el namespace entero); al restaurar el shim IA17
   * el error de tipo salió a la luz (tsc TS2339 en seller/productos/page).
   */
  async hardDelete(
    productId: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<{ productId: string; hardDeleted: true; imagesPurged: number }> {
    const url = sellerId
      ? `/seller/products/${encodeURIComponent(productId)}/hard-delete?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/${encodeURIComponent(productId)}/hard-delete`;
    return apiFetch<{ productId: string; hardDeleted: true; imagesPurged: number }>(url, {
      method: 'DELETE',
      signal,
    });
  },
  /**
   * POST /api/seller/products/:productId/images — upload up to 3 images
   * (multipart/form-data). The backend stores them in the configured
   * bucket (S3/R2/local fs — Fase 3 stub), updates the product's
   * `images` array, and returns the updated product.
   *
   * Uses `apiFetchFormData` (NOT `apiFetch`) because the body is a
   * `FormData` — `apiFetch` would force `Content-Type: application/json`
   * and break the multipart boundary. `apiFetchFormData` mirrors the
   * full `apiFetch` flow (auth header, 401→refresh→retry, network
   * error wrapping, envelope unwrap) but does NOT set Content-Type.
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only.
   *
   * Spec ref: SPEC-081 Fase 3 §5.2, §4.1 (uploadProductImages),
   *           §0 decision #8 (images uploaded post-creation),
   *           §6.6 (image uploader component).
   */
  async uploadImages(
    productId: string,
    formData: FormData,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<{ images: string[] }> {
    const url = sellerId
      ? `/seller/products/${encodeURIComponent(productId)}/images?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/${encodeURIComponent(productId)}/images`;
    return apiFetchFormData<{ images: string[] }>(url, {
      method: 'POST',
      body: formData,
      signal,
    });
  },
  /**
   * POST /api/seller/products/staged-images — STAGED upload, BEFORE the
   * product exists (founder directive 2026-10-05: "que todos los productos
   * desde su creación puedan subir sus fotos, sin tener que repetir el
   * proceso luego").
   *
   * The create flow uploads images here FIRST; the returned URLs are then
   * attached to `POST /api/seller/products` as `images[]`, so the product
   * is born WITH its photos (no post-create upload step — the
   * "imágenes a la primera" bug class is eliminated).
   *
   * Uses `apiFetchFormData` (multipart — same as uploadImages). No 2FA
   * (non-destructive). Rate limit: 60/hr (productImageLimiter).
   */
  async uploadStagedImages(
    formData: FormData,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE stages the images under the SELECTED sub-store's path
    // (NOT the primary store's path — the staged path is scoped to the
    // sellerId so a malicious user can't reference another seller's
    // staged URLs).
    sellerId?: string,
  ): Promise<{ images: string[] }> {
    const url = sellerId
      ? `/seller/products/staged-images?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/staged-images`;
    return apiFetchFormData<{ images: string[] }>(url, {
      method: 'POST',
      body: formData,
      signal,
    });
  },
  /**
   * DELETE /api/seller/products/:productId/images — delete a single
   * image by URL. The body is `{ imageUrl }` (JSON — NOT FormData).
   * The backend removes the URL from the product's `images` array +
   * deletes the underlying asset from the bucket (best-effort — if the
   * bucket delete fails, the URL is still removed from the array so the
   * seller UI is consistent).
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only.
   *
   * Spec ref: SPEC-081 Fase 3 §5.2, §4.1 (deleteProductImage),
   *           §6.6 (image uploader component).
   */
  /**
   * Sprint 139 (PR-K): generic image upload for coupon/gift card covers.
   * POST /api/seller/upload-image — uploads a file, returns CDN URL.
   */
  async uploadImage(
    formData: FormData,
  ): Promise<{ imageUrl: string }> {
    return apiFetchFormData<{ imageUrl: string }>('/seller/upload-image', {
      method: 'POST',
      body: formData,
    });
  },
  async deleteImage(
    productId: string,
    imageUrl: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<SellerProductResponse> {
    const url = sellerId
      ? `/seller/products/${encodeURIComponent(productId)}/images?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/${encodeURIComponent(productId)}/images`;
    return apiFetch<SellerProductResponse>(url, {
      method: 'DELETE',
      body: JSON.stringify({ imageUrl }),
      signal,
    });
  },
  /**
   * PR #1024: PATCH /api/seller/products/:productId/pause
   * Hide product from public catalog (status: 'approved' → 'paused').
   * The product is preserved (NOT deleted) — can be reactivated via unpause.
   * NO 2FA required (non-destructive).
   */
  async pauseProduct(
    productId: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<{ productId: string; status: 'paused' }> {
    const url = sellerId
      ? `/seller/products/${encodeURIComponent(productId)}/pause?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/${encodeURIComponent(productId)}/pause`;
    return apiFetch(url, {
      method: 'PATCH',
      signal,
    });
  },
  /**
   * PR #1024: PATCH /api/seller/products/:productId/unpause
   * Reactivate a paused product (status: 'paused' → 'approved').
   */
  async unpauseProduct(
    productId: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<{ productId: string; status: 'approved' }> {
    const url = sellerId
      ? `/seller/products/${encodeURIComponent(productId)}/unpause?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/products/${encodeURIComponent(productId)}/unpause`;
    return apiFetch(url, {
      method: 'PATCH',
      signal,
    });
  },
  /**
   * Convenience constant — the 6 valid `ProductStatus` values for the
   * seller dashboard status filter dropdown (avoids hard-coding the
   * union in the page component; the type itself is imported from
   * `@profileia/shared` so this array stays in sync with the union).
   *
   * §6 Anti-Invención: the values mirror `ProductStatus` verbatim
   * (Task A) — NOT invented here.
   *
   * Spec ref: SPEC-081 Fase 3 §6.3 (status filter), §3.1 (status enum).
   */
  STATUS_VALUES: [
    'pending_approval',
    'approved',
    'rejected',
    'paused',
    'out_of_stock',
    'deleted',
  ] as ReadonlyArray<ProductStatus>,

  /**
   * TASK-FEAT-PRODUCT-TRANSFER (2026-11-05): POST /api/seller/products/:productId/transfer
   * Transfer a product from one store/sub-store to another.
   *
   * Body: `{ targetSellerId: string }` — the destination store's sellerId.
   * Query: `?sellerId=<fromSellerId>` — the source store (optional, defaults
   * to the primary store if not provided).
   *
   * Returns: `{ productId, fromSellerId, toSellerId, slug, slugChanged,
   * previousSlug, status }`. If the slug collided in the destination store,
   * `slugChanged` is true + `slug` has the new suffix (e.g. `-2`).
   *
   * NO 2FA required (non-destructive — the product is moved, not deleted;
   * credits/wallet are NOT touched).
   */
  async transferProduct(
    productId: string,
    targetSellerId: string,
    signal?: AbortSignal,
    fromSellerId?: string,
  ): Promise<{
    productId: string;
    fromSellerId: string;
    toSellerId: string;
    slug: string;
    slugChanged: boolean;
    previousSlug: string;
    status: string;
  }> {
    const base = `/seller/products/${encodeURIComponent(productId)}/transfer`;
    const url = fromSellerId
      ? `${base}?sellerId=${encodeURIComponent(fromSellerId)}`
      : base;
    return apiFetch(url, {
      method: 'POST',
      body: JSON.stringify({ targetSellerId }),
      signal,
    });
  },
};
